import { defineStore } from "pinia";
import Network from "~/utils/network";
import { FLOW_POSITION } from "~~/utils/const";
import { useSimpleFieldStore } from "~/stores/simpleField";
import { useStatementPromptStore } from "~/stores/statementPrompt";
import { useDocumentPromptStore } from "~/stores/documentPrompt";
import { useSpecialFieldStore } from "~/stores/specialField";
import { useProfileStore } from "./profile";

const urls = {
  myBnplApplication: (publicId) => `/bnpl/customer/application/${publicId}`, // GET
  getFlow: (publicId) => `/bnpl/customer/application/${publicId}/flow`, // GET
  updateData: (publicId) => `/bnpl/customer/application/${publicId}/data`, // PATCH
  updatePosition: (publicId) =>
    `/bnpl/customer/application/${publicId}/update-position`, // PATCH
  nextStep: (publicId) => `/bnpl/customer/application/${publicId}/next-step`,
  introKyc: (publicId) => `/bnpl/customer/application/${publicId}/intro-kyc`, // GET
  close: (publicId) => `/bnpl/customer/application/${publicId}/close`, // POST
  closePublic: (publicId) => `/bnpl/external/${publicId}/close`, // POST
  requestDataChange: (publicId) =>
    `/bnpl/customer/application/${publicId}/data-change`, // POST
  startScoringEvaluation: (publicId) =>
    `/bnpl/customer/application/${publicId}/evaluate-scoring`,
  getScoringDecision: (publicId) =>
    `/bnpl/customer/application/${publicId}/scoring-decision`,
  sendSaveEmail: (publicId) =>
    `/bnpl/customer/application/save-payment-flow/${publicId}`,
  startKyc: (publicId) => `/bnpl/customer/application/${publicId}/kyc/start`, // POST
  kycStatus: (publicId) => `/bnpl/customer/application/${publicId}/kyc/status`, // POST
  uploadFile: (publicId) => `/bnpl/customer/application/${publicId}/document`, // PATCH
  getPreviousDocuments: (publicId, promptId) =>
    `/bnpl/customer/application/${publicId}/document/${promptId}`, // GET
  getSigningDocuments: (publicId) =>
    `/bnpl/customer/application/${publicId}/documents-for-signing`, // GET
  startSign: (publicId) =>
    `/bnpl/customer/application/${publicId}/signature-start`, // POST
  signWithSms: (publicId) =>
    `/bnpl/customer/application/${publicId}/validate-consent`, // POST
  getSignatureStatus: (publicId) =>
    `/bnpl/customer/application/${publicId}/signature-status`, // GET
  fetchTemplate: (template) => `/administration/template-render/${template}`, // GET//TODO country id

  signatureErrorFound: `/credit/bnpl-application-signature/error-found`,
  pollStatus: `/credit/my-bnpl-application/status`, // GET
};

let loadEverythingInFlight = null;

/**
 * BNPL Application store
 */

export const useBnplApplicationStore = defineStore("bnplApplication", {
  state: () => {
    return {
      publicId: "",
      financier: "",
      bnplApplication: {},
      fallbackBnplApplication: {},
      bnplApplicationWithId: [],
      selectedOffer: {},
      flow: [],
      applicationData: {},
      signatureProcess: {},
      comnicaRecord: {},
      loaded: false,
      kycMobile: false,
      signMobile: false,
      hasScoring: false,
      loadingFallbackOffer: false,
      introKycRequired: false,
    };
  },
  actions: {
    // The page asks for a load on every re-render until the first one finishes. Loads running side
    // by side can let an older application response overwrite a newer position, so concurrent
    // calls share one load.
    loadEverything: function (route) {
      if (!loadEverythingInFlight) {
        loadEverythingInFlight = (async () => {
          await this.fetchApplication(route);
          await this.fetchFlow();
          await this.fetchIntroKyc();
          this.setLoaded();
          return true;
        })().finally(() => {
          loadEverythingInFlight = null;
        });
      }
      return loadEverythingInFlight;
    },
    /**
     * Whether the intro has to be replaced by an identification. Re-run after the KYC finishes:
     * it flips back to false and, for a customer in arrears, that is what lets the risky-user
     * screen take over again.
     */
    fetchIntroKyc: async function () {
      try {
        const data = await Network.http(urls.introKyc(this.publicId), {
          method: "GET",
        });
        this.introKycRequired = !!data.required;
        return data;
      } catch (err) {
        console.log(err);
        this.introKycRequired = false;
      }
    },
    resetEverything: async function () {
      this.publicId = "";
      this.financier = "";
      this.bnplApplication = {};
      this.fallbackBnplApplication = {};
      this.bnplApplicationWithId = [];
      this.selectedOffer = {};
      this.flow = [];
      this.applicationData = {};
      this.signatureProcess = {};
      this.comnicaRecord = {};
      this.loaded = false;
      this.kycMobile = false;
      this.signMobile = false;
      this.hasScoring = false;
      this.loadingFallbackOffer = false;
      this.introKycRequired = false;
    },
    fetchSignatureProcess: async function () {
      try {
        let data = await Network.http(urls.getSigningDocuments(this.publicId), {
          method: "GET",
        });
        this.saveSignatureProcess(data);
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    fetchPreviousDocuments: async function (promptId) {
      try {
        return await Network.http(
          urls.getPreviousDocuments(this.publicId, promptId),
          { method: "GET" }
        );
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },

    fetchApplication: async function (route) {
      try {
        const data = await Network.http(urls.myBnplApplication(this.publicId), {
          method: "GET",
        });
        data.deal.transactions.sort((a, b) => {
          return a.seq - b.seq;
        });

        if (
          !!data.preScore &&
          route &&
          route.params &&
          !route.name.includes("financier-prescore")
        ) {
          window.location.href =
            "/" + route.params.financier + "/prescore/" + route.params.publicId;
        }
        if (
          !data.preScore &&
          route &&
          route.params &&
          route.name !== "financier-publicId"
        ) {
          window.location.href =
            "/" + route.params.financier + "/" + route.params.prescoreId;
        }
        if (
          data.deal &&
          data.deal.segment &&
          data.deal.segment === BNPL_SEGMENT.BUSINESS
        ) {
          window.location.href = "/b2b/" + this.financier + "/" + this.publicId;
        }
        this.saveBnplApplication(data);
        return data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    fetchAndGetScoringDecision: async function () {
      try {
        return await Network.http(urls.getScoringDecision(this.publicId), {
          method: "GET",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },

    fetchFlow: async function () {
      try {
        Network.http(urls.getFlow(this.publicId), { method: "GET" }).then(
          (data) => {
            this.saveFlow(data);
            this.hasScoring = data.hasScoring;
            return data;
          }
        );
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    pollStatus: async function () {
      try {
        const { data } = await Network.http(urls.pollStatus, { method: "GET" });
        this.updateStatus(data);
        return Promise.resolve(data);
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    saveDynamicData: async function ({ key, value }) {
      try {
        this.updateDynamicData({ key, value });
        await Network.http(urls.updateData(this.publicId), {
          body: {
            key,
            value,
          },
          method: "PATCH",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    close: async function () {
      try {
        await Network.http(urls.close(this.publicId), { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    closePublic: async function () {
      try {
        await Network.http(urls.closePublic(this.publicId), { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    requestDataChange: async function () {
      try {
        await Network.http(urls.requestDataChange(this.publicId), {
          method: "POST",
        });
        await this.fetchApplication();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    finish: async function () {
      try {
        await Network.http(urls.finish, { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    signatureErrorFound: async function ({}, request) {
      try {
        await Network.http(urls.signatureErrorFound, request, {
          method: "PATCH",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    savePosition: async function ({
      flowPosition,
      currentFlowStepIndex,
      stepIdentifier,
    }) {
      try {
        await Network.http(urls.updatePosition(this.publicId), {
          body: {
            flowPosition: flowPosition,
            currentFlowIndex: currentFlowStepIndex,
            stepIdentifier: stepIdentifier,
          },
          method: "PATCH",
        });

        this.updatePosition({
          flowPosition,
          currentFlowStepIndex,
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    /**
     * @param fromCurrent send the position shown on screen. If the application has already left it
     *   (a double click, the intro auto-advancing twice), the server answers with where the
     *   application really is instead of stepping again. Only for screens whose position the
     *   server never changes on its own.
     */
    nextStep: async function ({ fromCurrent = false } = {}) {
      const body = { stepBack: false };
      if (fromCurrent && this.bnplApplication.currentPosition) {
        body.fromPosition = this.bnplApplication.currentPosition;
        body.fromFlowIndex = this.bnplApplication.currentFlowIndex;
      }
      try {
        const result = await Network.http(urls.nextStep(this.publicId), {
          body,
          method: "POST",
        });
        this.updatePosition({
          flowPosition: result.flowPosition,
          currentFlowStepIndex: result.currentFlowIndex,
        });
        if (result.flowPosition === FLOW_POSITION.INTRO) {
          await this.fetchIntroKyc();
        }
      } catch (err) {
        console.log(err);
        await this.syncPosition();
        await Promise.reject(err);
      }
    },
    // A failed step can mean the screen is behind the application, so take the position from the
    // server instead of leaving the customer on a step that was already passed.
    syncPosition: async function () {
      try {
        const data = await Network.http(urls.myBnplApplication(this.publicId), {
          method: "GET",
        });
        if (
          data.currentPosition !== this.bnplApplication.currentPosition ||
          data.currentFlowIndex !== this.bnplApplication.currentFlowIndex
        ) {
          this.updatePosition({
            flowPosition: data.currentPosition,
            currentFlowStepIndex: data.currentFlowIndex,
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    stepBack: async function () {
      try {
        const result = await Network.http(urls.nextStep(this.publicId), {
          body: {
            stepBack: true,
          },
          method: "POST",
        });
        this.updatePosition({
          flowPosition: result.flowPosition,
          currentFlowStepIndex: result.currentFlowIndex,
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    setKycMobile: function (val) {
      this.updateKycMobile(val);
    },
    setSignMobile: function (val) {
      this.updateSignMobile(val);
    },
    startKyc: async function () {
      try {
        let data = await Network.http(urls.startKyc(this.publicId), {
          method: "POST",
        });

        this.saveComnica(data);
        return data;
        /*this.updatePosition({
                    flowPosition: "KYC_WAITING",
                    currentFlowStepIndex: false,
                });*/
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    getKycStatus: async function () {
      try {
        return await Network.http(urls.kycStatus(this.publicId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    startSign: async function (request) {
      try {
        let data = await Network.http(urls.startSign(this.publicId), {
          method: "POST",
          body: request,
        });
        this.saveComnica(data);
        return data;
        /*
                this.updatePosition({
                    flowPosition: "SIGN_WAITING",
                    currentFlowStepIndex: false,
                });
                */
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    signWithSms: async function (requestBody) {
      try {
        const response = await Network.http(urls.signWithSms(this.publicId), {
          method: "POST",
          body: requestBody,
        });
        return response;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      } finally {
        fbq("trackCustom", "IcBnplContractSigned");
      }
    },
    getSignatureStatus: async function () {
      try {
        return await Network.http(urls.getSignatureStatus(this.publicId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    startScoringEvaluation: async function () {
      try {
        await Network.http(urls.startScoringEvaluation(this.publicId), {
          method: "PATCH",
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    sendSaveEmail: async function (requestBody) {
      try {
        return await Network.http(urls.sendSaveEmail(this.publicId), {
          method: "POST",
          body: requestBody,
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    getTemplate: async function (template) {
      try {
        return await Network.http(urls.fetchTemplate(template));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    uploadFile: async function ({
      file,
      promptId,
      promptIdentifier,
      documentName,
    }) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("promptId", promptId);
        formData.append("promptIdentifier", promptIdentifier);
        await Network.http(urls.uploadFile(this.publicId), {
          method: "PATCH",
          body: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    saveBnplApplication: function (data) {
      this.applicationData = JSON.parse(data.dynamicData);
      this.bnplApplication = data;
      if (!this.bnplApplication.kycTypes) {
        this.bnplApplication.kycTypes = [];
      }
      if (!this.bnplApplication.sigTypes) {
        this.bnplApplication.sigTypes = [];
      }
      if (data.projectRecords && data.projectRecords.length)
        if (data.projectRecords.length === 1) {
          this.comnicaRecord = data.projectRecords[0];
        } else {
          let records = data.projectRecords.sort((a, b) => {
            return a.recordId - b.recordId;
          });
          this.comnicaRecord = records[records.length - 1];
        }
    },
    saveBnplApplications: function (data) {
      this.bnplApplications = data;
    },
    saveBnplApplicationWithId: function ({ data, id }) {
      this.bnplApplicationWithId = { id, data };
    },
    saveSignatureProcess: function (data) {
      this.signatureProcess = data;
    },
    saveOffer: function (offer) {
      this.selectedOffer = offer;
    },
    saveFlow: function (flow) {
      if (this.flow.length) {
        this.flow = [];
      }
      let steps = flow.steps.sort((a, b) => {
        return a.rowIndex - b.rowIndex;
      });
      for (let s in steps) {
        steps[s].questions = steps[s].questions.filter((q) => !q.hidden);
        if (steps[s].questions.length > 0) {
          this.flow.push(steps[s]);
        }
      }
      if (this.bnplApplication.currentFlowIndex > this.flow.length) {
        this.bnplApplication.currentFlowIndex--;
        if (this.bnplApplication.currentPosition === FLOW_POSITION.FLOW) {
          this.savePosition({
            flowPosition: FLOW_POSITION.FLOW,
            currentFlowStepIndex: this.bnplApplication.currentFlowIndex,
          });
        }
      }
    },
    updateDynamicData: function ({ key, value }) {
      this.applicationData[key] = value;
    },
    updatePosition: function ({ flowPosition, currentFlowStepIndex }) {
      this.bnplApplication.currentPosition = flowPosition;
      if (currentFlowStepIndex || currentFlowStepIndex === 0)
        this.bnplApplication.currentFlowIndex = currentFlowStepIndex;
    },
    setLoaded: function () {
      this.loaded = true;
    },
    updateKycMobile: function (val) {
      this.kycMobile = val;
    },
    updateSignMobile: function (val) {
      this.signMobile = val;
    },
    saveComnica: function (data) {
      this.comnicaRecord = data;
    },
    updateStatus: function (data) {
      this.bnplApplication.currentStatus = data.currentStatus;
      this.bnplApplication.currentStatusResult = data.currentStatusResult;
      this.bnplApplication.currentPosition = data.currentPosition;
    },
    getFlowQuestionAnswer: function (identifier) {
      return this.applicationData[identifier];
    },
    previousFlowStep: function () {
      this.bnplApplication.currentFlowIndex--;
      this.savePosition({
        flowPosition: FLOW_POSITION.FLOW,
        currentFlowStepIndex: this.bnplApplication.currentFlowIndex,
      });
    },
    nextFlowStep: function () {
      if (this.bnplApplication.currentFlowIndex < this.flow.length) {
        this.bnplApplication.currentFlowIndex++;
        this.savePosition({
          flowPosition: FLOW_POSITION.FLOW,
          currentFlowStepIndex: this.bnplApplication.currentFlowIndex,
          stepIdentifier:
            this.flow[this.bnplApplication.currentFlowIndex - 1]?.identifier,
        });
      } else if (
        this.bnplApplication.currentPosition === FLOW_POSITION.SUMMARY
      ) {
        if (this.hasScoring) {
          this.savePosition({ flowPosition: FLOW_POSITION.SCORING });
        } else {
          this.savePosition({ flowPosition: FLOW_POSITION.CONTRACT_CHECK });
        }
      } else {
        this.savePosition({ flowPosition: FLOW_POSITION.SUMMARY });
      }
    },
    goToScoring: function () {
      this.savePosition({ flowPosition: FLOW_POSITION.SCORING });
    },
    canContinue: function () {
      let step = this.bnplApplication.currentFlowIndex - 1;

      const simpleFieldStore = useSimpleFieldStore();
      const statementPromptStore = useStatementPromptStore();
      const documentPromptStore = useDocumentPromptStore();
      const specialFieldStore = useSpecialFieldStore();
      const profileStore = useProfileStore();

      for (let q in this.flow[step].questions) {
        let question = this.flow[step].questions[q];
        let field;
        switch (question.fieldType) {
          case FLOW_QUESTION_FIELD_TYPE.SIMPLE_FIELD:
            field = simpleFieldStore.fieldData[question.fieldId];
            break;
          case FLOW_QUESTION_FIELD_TYPE.STATEMENT_PROMPT:
            field = statementPromptStore.statementData[question.fieldId];
            break;
          case FLOW_QUESTION_FIELD_TYPE.DOCUMENT_PROMPT:
            field = documentPromptStore.documentData[question.fieldId];
            break;
          case FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD:
            field = specialFieldStore.specialFieldData[question.fieldId];
            break;
          case FLOW_QUESTION_FIELD_TYPE.READONLY_FIELD:
            continue;
        }
        if (question.isRequired) {
          let needAnswer;
          if (question.isConditional) {
            for (const condition of question.conditions) {
              let index = question.conditions.indexOf(condition);

              let dependency =
                simpleFieldStore.fieldData[condition.conditionalFieldId];

              let evaluate;

              let answer = this.applicationData[dependency.identifier];
              if (dependency.type === SIMPLE_FIELD_TYPE.CHECKBOX) {
                if (answer) {
                  let answerObject = JSON.parse(answer);
                  evaluate = answerObject.includes(
                    condition.conditionalFieldAnswer
                  );
                } else {
                  evaluate = false;
                }
              } else {
                evaluate = answer === condition.conditionalFieldAnswer;
              }

              if (index === 0) {
                needAnswer = evaluate;
              } else {
                let operator =
                  question.conditions[index - 1].conditionalFieldOperator;
                if (CONDITIONAL_FIELD_OPERATOR.ENUM.AND === operator) {
                  needAnswer = needAnswer && evaluate;
                } else if (CONDITIONAL_FIELD_OPERATOR.ENUM.OR === operator) {
                  needAnswer = needAnswer || evaluate;
                }
              }
            }

            if (needAnswer) {
              if (question.fieldType === FLOW_QUESTION_FIELD_TYPE.PSD2_FIELD) {
                if (!field.completed) {
                  console.warn("Conditionally required PSD2 not synced!");
                  return false;
                }
              } else if (
                question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD &&
                question.fieldSubtype ===
                  SPECIAL_FIELD_TYPE.CEGADATAPI_ENTREPRENEUR
              ) {
                if (!this.applicationData["entrepreneur_vat"]) {
                  console.warn(
                    "Conditionally required entrepreneur field failed!"
                  );
                  return false;
                }
              } else {
                if (!this.applicationData[field.identifier]) {
                  console.warn("Conditionally required failed!");
                  return false;
                }
              }

              if (question.isHardKo) {
                if (
                  this.applicationData[field.identifier] === question.koAnswer
                ) {
                  console.warn("Conditionally KO failed!");
                  return false;
                }
              }
              if (
                field &&
                field.type === SIMPLE_FIELD_TYPE.INPUT &&
                field.validation
              ) {
                let value = this.applicationData[field.identifier];

                if (value === undefined) {
                  value = "";
                }

                const regex = new RegExp(field.validation);
                if (!regex.test(value)) {
                  console.warn("Conditionally regex validation failed!");
                  return false;
                }
              }
            }
          } else {
            if (question.fieldType === FLOW_QUESTION_FIELD_TYPE.SPECIAL_FIELD) {
              if (
                question.fieldSubtype === SPECIAL_FIELD_TYPE.PSD2 &&
                !field.completed
              ) {
                console.warn("Required PSD2 not synced!");
                return false;
              }
              switch (question.fieldSubtype) {
                case SPECIAL_FIELD_TYPE.CUSTOMER_FIRST_NAME:
                  if (!profileStore.firstName) return false;
                  break;
                case SPECIAL_FIELD_TYPE.CUSTOMER_LAST_NAME:
                  if (!profileStore.lastName) return false;
                  break;
                case SPECIAL_FIELD_TYPE.BIRTH_DATE:
                  if (!profileStore.dateOfBirth) return false;
                  break;
                case SPECIAL_FIELD_TYPE.BIRTH_PLACE:
                  if (!profileStore.placeOfBirth) return false;
                  break;
                case SPECIAL_FIELD_TYPE.MOTHERS_NAME:
                  if (!profileStore.mothersName) return false;
                  break;
                case SPECIAL_FIELD_TYPE.POST_CODE:
                  if (!profileStore.postCode) return false;
                  break;
                case SPECIAL_FIELD_TYPE.CITY:
                  if (!profileStore.city) return false;
                  break;
                case SPECIAL_FIELD_TYPE.STREET:
                  if (!profileStore.street) return false;
                  break;
                case SPECIAL_FIELD_TYPE.HOUSE_NUMBER:
                  if (!profileStore.houseNumber) return false;
                  break;
                case SPECIAL_FIELD_TYPE.CEGADATAPI_ENTREPRENEUR:
                  if (!this.applicationData["entrepreneur_vat"]) return false;
                  break;
              }
            } else {
              if (!this.applicationData[field.identifier]) {
                console.warn("Required failed!");
                return false;
              }
            }

            if (question.isHardKo) {
              if (
                this.applicationData[field.identifier] === question.koAnswer
              ) {
                console.warn("KO failed!");
                return false;
              }
            }
            if (
              field &&
              field.type === SIMPLE_FIELD_TYPE.INPUT &&
              field.validation
            ) {
              let value = this.applicationData[field.identifier];

              if (value === undefined) {
                value = "";
              }

              const regex = new RegExp(field.validation);
              if (!regex.test(value)) {
                console.warn("Regex validation failed!");
                return false;
              }
            }
          }
        }
      }
      return true;
    },
    stepCount: function () {
      /* +1 summary +1 sign +1 firstPayment */
      return this.flow.length + 3 + this.hasScoring;
    },
    currentStep: function () {
      if (this.bnplApplication.currentPosition === FLOW_POSITION.FLOW) {
        return this.bnplApplication.currentFlowIndex;
      } else {
        let totalSteps = this.flow.length;
        if (this.bnplApplication.currentPosition === FLOW_POSITION.SUMMARY) {
          return totalSteps + 1;
        }
        if (this.bnplApplication.currentPosition === FLOW_POSITION.SCORING) {
          return totalSteps + 2;
        }
        if (
          this.bnplApplication.currentPosition === FLOW_POSITION.CONTRACT_CHECK
        ) {
          return totalSteps + 2 + this.hasScoring;
        }
        if (
          this.bnplApplication.currentPosition === FLOW_POSITION.FIRST_PAYMENT
        ) {
          return totalSteps + 3 + this.hasScoring;
        }
      }
    },
    currentStepDuration: function () {
      if (this.flow.length) {
        if (this.bnplApplication.currentPosition === FLOW_POSITION.FLOW) {
          if (this.flow[this.bnplApplication.currentFlowIndex - 1]) {
            return this.flow[this.bnplApplication.currentFlowIndex - 1]
              .estimatedTimeToAnswerInSeconds;
          }
          return null;
        } else {
          return 120;
        }
      } else {
        return 0;
      }
    },
    fetchFallbackApplication: async function (publicId) {
      try {
        const data = await Network.http(urls.myBnplApplication(publicId), {
          method: "GET",
        });
        data.deal.transactions.sort((a, b) => {
          return a.seq - b.seq;
        });
        this.saveFallbackBnplApplication(data);
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    saveFallbackBnplApplication: function (data) {
      this.fallbackBnplApplication = data;
    },
  },
  getters: {
    getActiveOffer: function () {
      return this.selectedOffer;
    },
    getActiveApplication: function () {
      return this.bnplApplication;
    },
    getApplicationWithId: function (id) {
      return this.bnplApplicationWithId[id];
    },
    isKycMobile: function () {
      return this.kycMobile;
    },
    getSignatureProcess: function () {
      return this.signatureProcess;
    },
    isSignMobile: function () {
      return this.signMobile;
    },
    applicationStatus: function () {
      return this.bnplApplication.currentStatus;
    },
    applicationStatusResult: function () {
      return this.bnplApplication.currentStatusResult;
    },
    getBnplApplications: function () {
      return this.bnplApplications;
    },
    comnicaUrl: function () {
      return this.comnicaRecord.url;
    },
    showProgressBar: function () {
      return (
        this.flow.length &&
        (this.bnplApplication.currentPosition === FLOW_POSITION.FLOW ||
          this.bnplApplication.currentPosition === FLOW_POSITION.SUMMARY ||
          this.bnplApplication.currentPosition === FLOW_POSITION.SCORING ||
          this.bnplApplication.currentPosition ===
            FLOW_POSITION.CONTRACT_CHECK ||
          this.bnplApplication.currentPosition === FLOW_POSITION.FIRST_PAYMENT)
      );
    },
    payInFullEnabled() {
      try {
        return (
          this.bnplApplication.finance.payInFullEnabled &&
          !this.bnplApplication.preScore
        );
      } catch {
        return false;
      }
    },
    payInFullAmount() {
      return this.bnplApplication.finance.payInFullAmount ?? 0;
    },
    isPayInFull() {
      return this.bnplApplication?.finance?.usePayInFull ?? false;
    },
    useCoverageCheck() {
      try {
        return this.bnplApplication.finance.useCoverageCheck;
      } catch {
        return false;
      }
    },
  },
});
